import { createContext, use } from 'react';
import type { SectionDto } from '@atnt/shared';
import { fileUrl as defaultFileUrl } from '../file-url';
import { DEFAULT_TIME_ZONE } from '../live/format';

/**
 * Render settings shared by every dashboard element renderer.
 *  - `variant`: `viewer` = the viewer look of 14:7285 (glass, Montserrat); `canvas` = the constructor look (317:6923).
 *  - `mode`: `view` (links/autoplay active), `preview` (editor "Ko'rish": like view, no navigation side effects
 *    unless handlers are given), `edit` (static: no navigation, videos paused, empty placeholders shown).
 */
export type RenderVariant = 'viewer' | 'canvas';
export type RenderMode = 'view' | 'preview' | 'edit';

export interface SceneContextValue {
  variant: RenderVariant;
  mode: RenderMode;
  /** sections in card order (the section grid and marker links) */
  sections: readonly SectionDto[];
  /** reservoir time zone for clock/date widgets */
  timeZone: string;
  fileUrl: (fileId: string) => string;
  /** logo of the viewer title block (14:7285); shown on the first title element only */
  brandLogoUrl: string | null;
  /** click on a section card / a marker linked to a section */
  onOpenSection?: (sectionId: string) => void;
  /** anchors of the visible markers — viewer labels flip sides to avoid covering another marker */
  markers: readonly MarkerAnchor[];
}

export interface MarkerAnchor {
  id: string;
  x: number;
  y: number;
  scale: number;
}

export const DEFAULT_SCENE: SceneContextValue = {
  variant: 'viewer',
  mode: 'view',
  sections: [],
  timeZone: DEFAULT_TIME_ZONE,
  fileUrl: defaultFileUrl,
  brandLogoUrl: null,
  markers: [],
};

const SceneContext = createContext<SceneContextValue>(DEFAULT_SCENE);

export const SceneProvider = SceneContext;

export function useScene(): SceneContextValue {
  return use(SceneContext);
}
